import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import samplesService, { SampleListResponse, SampleStatus } from '../services/samplesService';
import { clientsService } from '../services/clientsService';

/**
 * Optimized hook for Reception dashboard data
 * CRITICAL: This hook is designed for the Reception dashboard ONLY
 * It handles the most critical path in the application - sample creation flow
 * 
 * Safety features:
 * - Manual refresh only (no auto-refresh to avoid interfering with form filling)
 * - Minimal caching (5 minutes for clients, 30 seconds for samples)
 * - Immediate refresh after sample creation
 * - Fallback to direct API calls if any issues
 */
export function useOptimizedReceptionData() {
  const [samples, setSamples] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    today_received: 0,
    pending_reception: 0,
    urgent_samples: 0,
  });

  // Cache management
  const samplesCache = useRef<{ data: any[]; timestamp: number } | null>(null);
  const clientsCache = useRef<{ data: any[]; timestamp: number } | null>(null);
  const loadingRef = useRef<boolean>(false);

  // Cache TTL constants
  const SAMPLES_CACHE_TTL = 30000; // 30 seconds - short cache for samples
  const CLIENTS_CACHE_TTL = 300000; // 5 minutes - longer cache for clients (rarely change)

  /**
   * Load Reception samples (RECEPTION status only)
   * Uses short-term caching to avoid duplicate requests
   */
  const loadSamples = useCallback(async (force: boolean = false): Promise<any[]> => {
    const now = Date.now();
    
    // Check cache first (unless forced refresh)
    if (!force && samplesCache.current) {
      const age = now - samplesCache.current.timestamp;
      if (age < SAMPLES_CACHE_TTL) {
        return samplesCache.current.data;
      }
    }

    try {
      const response = await samplesService.getSamples(1, 50, {
        status: [SampleStatus.RECEPTION],
      });
      
      const samplesData = response.samples || [];
      
      // Update cache
      samplesCache.current = {
        data: samplesData,
        timestamp: now,
      };
      
      return samplesData;
    } catch (error) {
      console.error('Error loading reception samples:', error);
      throw error;
    }
  }, []);

  /**
   * Load active clients with longer caching
   * Clients change infrequently, so we can cache them longer
   */
  const loadClients = useCallback(async (force: boolean = false): Promise<any[]> => {
    const now = Date.now();
    
    // Check cache first (unless forced refresh)
    if (!force && clientsCache.current) {
      const age = now - clientsCache.current.timestamp;
      if (age < CLIENTS_CACHE_TTL) {
        return clientsCache.current.data;
      }
    }

    try {
      const clientsData = await clientsService.getActiveClients();
      
      // Update cache
      clientsCache.current = {
        data: clientsData || [],
        timestamp: now,
      };
      
      return clientsData || [];
    } catch (error) {
      console.error('Error loading clients:', error);
      // Return empty array if clients fail - Reception can still work
      return [];
    }
  }, []);

  /**
   * Calculate statistics from samples data
   * Memoized to prevent recalculation on every render
   */
  const calculateStats = useMemo(() => {
    if (!samples.length) {
      return {
        today_received: 0,
        pending_reception: 0,
        urgent_samples: 0,
      };
    }

    const urgentSamples = samples.filter(
      sample => sample.priorite === 'URGENTE'
    ).length;
    
    // Get today's date for filtering
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const todayReceived = samples.filter(sample => {
      const sampleDate = new Date(sample.date_reception || sample.date_creation);
      sampleDate.setHours(0, 0, 0, 0);
      return sampleDate.getTime() === today.getTime();
    }).length;
    
    return {
      today_received: todayReceived,
      pending_reception: samples.length,
      urgent_samples: urgentSamples,
    };
  }, [samples]);

  /**
   * Load all Reception data
   * CRITICAL: This function must be reliable - it's the core of Reception workflow
   */
  const loadReceptionData = useCallback(async (force: boolean = false) => {
    // Prevent concurrent loads
    if (loadingRef.current && !force) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      // Load samples and clients in parallel
      const [samplesData, clientsData] = await Promise.all([
        loadSamples(force),
        loadClients(force),
      ]);

      setSamples(samplesData);
      setClients(clientsData);
      
    } catch (error) {
      console.error('Error loading reception data:', error);
      setError('Erreur lors du chargement des données');
      
      // Fallback: try to use cached data if available
      if (samplesCache.current) {
        setSamples(samplesCache.current.data);
      }
      if (clientsCache.current) {
        setClients(clientsCache.current.data);
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [loadSamples, loadClients]);

  /**
   * Refresh data manually
   * Used after sample creation or when user clicks refresh
   */
  const refresh = useCallback(() => {
    return loadReceptionData(true);
  }, [loadReceptionData]);

  /**
   * Clear caches (useful for testing or when data might be stale)
   */
  const clearCache = useCallback(() => {
    samplesCache.current = null;
    clientsCache.current = null;
  }, []);

  // Initial data load
  useEffect(() => {
    loadReceptionData();
  }, [loadReceptionData]);

  // Update stats when samples change
  useEffect(() => {
    setStats(calculateStats);
  }, [calculateStats]);

  return {
    samples,
    clients,
    loading,
    error,
    stats,
    refresh,
    clearCache,
    // Expose cache status for debugging
    cacheStatus: {
      samplesAge: samplesCache.current ? Date.now() - samplesCache.current.timestamp : null,
      clientsAge: clientsCache.current ? Date.now() - clientsCache.current.timestamp : null,
    },
  };
}